import {
  findPlayingTimelineIndexByVideoTime,
  findPlayingTimelineIndexByTimelineTime,
} from './findPlayingTimeline';
import getStreamTimelines from './getStreamTimelines';

const defaultState = {
  url: null,
  isPlaying: false,
  playingRequest: null,
  nextFragmentIndex: 0,
  startProgramDateTime: 0,
  endProgramDateTime: 0,
  videoDuration: 0,
  timelineDuration: 0,
  playheadVideoTime: 0,
  playheadTimelineTime: 0,
  playheadTimelineIndex: null,
  contentTimelines: [],
  blackholeTimelines: [],
  seekRequestVideoTime: null,
  markInTimelineTime: 0,
  markOutTimelineTime: 0,
};

const getters = {
  playheadTimelineTimePercent({ playheadTimelineTime, timelineDuration }) {
    return timelineDuration > 0 ?
      playheadTimelineTime / timelineDuration * 100
      : 0;
  },
  playheadProgramDateTime({
    contentTimelines,
    playheadTimelineTime,
    playheadTimelineIndex,
  }) {
    if (playheadTimelineIndex === null
      || playheadTimelineIndex < 0
      || contentTimelines.length < 1) {
      return 0;
    }
    const timeline = contentTimelines[playheadTimelineIndex];
    return timeline.startProgramDateTime + (playheadTimelineTime - timeline.startTimelineTime);
  },
  markInProgramDateTime({
    contentTimelines,
    markInTimelineTime,
    startProgramDateTime,
  }) {
    if (contentTimelines.length < 1) {
      return startProgramDateTime;
    }
    const index = findPlayingTimelineIndexByTimelineTime(markInTimelineTime, contentTimelines);
    if (index < 0) {
      return startProgramDateTime;
    }
    const timeline = contentTimelines[index];
    return timeline.startProgramDateTime + (markInTimelineTime - timeline.startTimelineTime);
  },
  markOutProgramDateTime({
    contentTimelines,
    markOutTimelineTime,
    startProgramDateTime,
  }) {
    if (contentTimelines.length < 1) {
      return startProgramDateTime;
    }
    const index = findPlayingTimelineIndexByTimelineTime(markOutTimelineTime, contentTimelines);
    if (index < 0) {
      return startProgramDateTime;
    }
    const timeline = contentTimelines[index];
    return timeline.startProgramDateTime + (markOutTimelineTime - timeline.startTimelineTime);
  },
  isPlaying: state => state.isPlaying,
};

const mutations = {
  resetStream(state) {
    state.url = null;
    state.requestStartPlaying = true;
    state.nextFragmentIndex = 0;
    state.startProgramDateTime = 0;
    state.endProgramDateTime = 0;
    state.videoDuration = 0;
    state.timelineDuration = 0;
    state.playheadVideoTime = 0;
    state.playheadTimelineTime = 0;
    state.playheadTimelineIndex = null;
    state.contentTimelines = [];
    state.blackholeTimelines = [];
    state.markInTimelineTime = 0;
    state.markOutTimelineTime = 0;
  },
  loadStreamByUrl(state, { url }) {
    state.url = url;
    state.requestStartPlaying = true;
    state.nextFragmentIndex = 0;
    state.startProgramDateTime = 0;
    state.endProgramDateTime = 0;
    state.videoDuration = 0;
    state.timelineDuration = 0;
    state.playheadVideoTime = 0;
    state.playheadTimelineTime = 0;
    state.playheadTimelineIndex = null;
    state.contentTimelines = [];
    state.blackholeTimelines = [];
    state.markInTimelineTime = 0;
    state.markOutTimelineTime = 0;
  },
  updateDurationAndTimelinesFromFragments(state, { data }) {
    const { fragments } = data.details;
    const {
      nextFragmentIndex,
      startProgramDateTime,
      endProgramDateTime,
      videoDuration,
      timelineDuration,
      contentTimelines,
      blackholeTimelines,
    } = getStreamTimelines(fragments, state);
    state.nextFragmentIndex = nextFragmentIndex;
    state.startProgramDateTime = startProgramDateTime;
    state.endProgramDateTime = endProgramDateTime;
    state.videoDuration = videoDuration;
    state.timelineDuration = timelineDuration;
    state.contentTimelines = contentTimelines;
    state.blackholeTimelines = blackholeTimelines;
  },
  setPlayheadByVideoTime(state, videoTime) {
    const playingTimelineIndex = findPlayingTimelineIndexByVideoTime(
      videoTime,
      state.contentTimelines,
    );
    if (playingTimelineIndex < 0) {
      return;
    }

    const timeline = state.contentTimelines[playingTimelineIndex];
    state.playheadTimelineTime = timeline.startTimelineTime + (videoTime - timeline.startVideoTime);
    state.playheadVideoTime = videoTime;
    state.playheadTimelineIndex = playingTimelineIndex;
  },
  setPlayheadByTimelineTime(state, timelineTime) {
    const playingTimelineIndex = findPlayingTimelineIndexByTimelineTime(
      timelineTime,
      state.contentTimelines,
    );
    if (playingTimelineIndex < 0) {
      return;
    }

    const timeline = state.contentTimelines[playingTimelineIndex];
    state.seekRequestVideoTime = timeline.startVideoTime
      + (timelineTime - timeline.startTimelineTime);
    state.playheadTimelineTime = timelineTime;
    state.playheadTimelineIndex = playingTimelineIndex;
  },
  seekPlayheadBackward(state, durationInMs) {
    const timelineTime = Math.max(
      0,
      state.playheadTimelineTime - durationInMs,
    );
    const playingTimelineIndex = findPlayingTimelineIndexByTimelineTime(
      timelineTime,
      state.contentTimelines,
    );
    if (playingTimelineIndex < 0) {
      return;
    }

    const timeline = state.contentTimelines[playingTimelineIndex];
    state.seekRequestVideoTime = timeline.startVideoTime
      + (timelineTime - timeline.startTimelineTime);
    state.playheadTimelineTime = timelineTime;
    state.playheadTimelineIndex = playingTimelineIndex;
  },
  seekPlayheadForward(state, durationInMs) {
    const timelineTime = Math.min(
      state.timelineDuration,
      state.playheadTimelineTime + durationInMs,
    );
    const playingTimelineIndex = findPlayingTimelineIndexByTimelineTime(
      timelineTime,
      state.contentTimelines,
    );
    if (playingTimelineIndex < 0) {
      return;
    }

    const timeline = state.contentTimelines[playingTimelineIndex];
    state.seekRequestVideoTime = timeline.startVideoTime
      + (timelineTime - timeline.startTimelineTime);
    state.playheadTimelineTime = timelineTime;
    state.playheadTimelineIndex = playingTimelineIndex;
  },
  seekRequestComplete(state) {
    state.seekRequestVideoTime = null;
  },
  setMarkInTimelineTime(state, timelineTime) {
    if (state.markOutTimelineTime > timelineTime) {
      state.markInTimelineTime = timelineTime;
      return;
    }

    const expectedClipDuration = state.markOutTimelineTime - state.markInTimelineTime;
    const newMarkOutTimelineTime = Math.min(
      timelineTime + expectedClipDuration,
      state.timelineDuration,
    );
    state.markInTimelineTime = timelineTime;
    state.markOutTimelineTime = newMarkOutTimelineTime;
  },
  setMarkOutTimelineTime(state, timelineTime) {
    if (state.markInTimelineTime < timelineTime) {
      state.markOutTimelineTime = timelineTime;
      return;
    }

    const expectedClipDuration = state.markOutTimelineTime - state.markInTimelineTime;
    const newMarkInTimelineTime = Math.max(
      timelineTime - expectedClipDuration,
      0,
    );
    state.markInTimelineTime = newMarkInTimelineTime;
    state.markOutTimelineTime = timelineTime;
  },
  requestStartPlaying(state, timelineTime) {
    if (timelineTime) {
      const playingTimelineIndex = findPlayingTimelineIndexByTimelineTime(
        timelineTime,
        state.contentTimelines,
      );
      if (playingTimelineIndex < 0) {
        return;
      }

      const timeline = state.contentTimelines[playingTimelineIndex];
      state.seekRequestVideoTime = timeline.startVideoTime
        + (timelineTime - timeline.startTimelineTime);
    }
    state.playingRequest = true;
  },
  requestStopPlaying(state) {
    state.playingRequest = false;
  },
  requestTogglePlaying(state) {
    state.playingRequest = !state.isPlaying;
  },
  setIsPlaying(state, isPlaying) {
    state.isPlaying = isPlaying;
    state.playingRequest = null;
  },
};

export default {
  state: defaultState,
  getters,
  mutations,
};
